import React, { useContext, useEffect, useMemo, useRef } from "react";
import { NavLink, useLocation } from 'react-router-dom';
import { getCDNImage } from "../../globals";
import { i18nValue, mergeHotelAndCheckinPlaceholders } from "../../functions";
import processHtml from "../processHtml";
import { HotelContext } from "../../context/hotel";
import { CheckInContext } from "../../context/checkin";
import MenuCommerceModule from "../MenuCommerce/MenuCommerceModule";

const getBlockImage = (value) => {
  if (!value) {
    return "";
  }

  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) {
    return value;
  }

  return getCDNImage(value);
};

const getVideoEmbedUrl = (url = "") => {
  const value = String(url || "").trim();

  if (!value) {
    return "";
  }

  if (value.includes("youtube.com/embed/") || value.includes("player.vimeo.com/video/")) {
    return value;
  }

  if (value.includes("youtube.com") || value.includes("youtu.be/")) {
    try {
      const parsedUrl = new URL(value);
      const youtubeId =
        parsedUrl.searchParams.get("v") || value.split("youtu.be/")[1]?.split("?")[0] || "";
      return youtubeId ? `https://www.youtube.com/embed/${youtubeId}` : "";
    } catch (error) {
      return "";
    }
  }

  if (value.includes("vimeo.com/")) {
    const vimeoId = value.split("vimeo.com/")[1]?.split("?")[0] || "";
    return vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : "";
  }

  return value;
};

const getFontSizeValue = (size = "medium") => {
  if (size === "small") {
    return 14;
  }
  if (size === "large") {
    return 18;
  }
  return 16;
};

const getBlockSpacing = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;

const normalizeContentBlocks = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  return [];
};

const getScrollableParent = (element) => {
  if (!element || typeof window === "undefined") {
    return null;
  }

  let current = element.parentElement;
  while (current && current !== document.body) {
    const styles = window.getComputedStyle(current);
    const overflowY = styles.overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }

  return document.scrollingElement || document.documentElement;
};

const getButtonStyles = (variant = "primary", align = "left") => {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 46,
    borderRadius: 12,
    padding: "0 18px",
    fontWeight: 700,
    textDecoration: "none",
    marginTop: 0,
    marginBottom: 0,
  };

  const wrapper = {
    textAlign: align,
  };

  if (variant === "secondary") {
    return {
      wrapper,
      button: {
        ...base,
        background: "#EEF2FF",
        color: "#82378A",
        border: "1px solid rgba(130,55,138,0.18)",
      },
    };
  }

  if (variant === "outline") {
    return {
      wrapper,
      button: {
        ...base,
        background: "#FFFFFF",
        color: "#82378A",
        border: "1px solid #82378A",
      },
    };
  }

  return {
    wrapper,
    button: {
      ...base,
      background: "#82378A",
      color: "#FFFFFF",
      border: "1px solid #82378A",
    },
  };
};

const getCustomButtonStyles = (block) => {
  const styles = getButtonStyles(block.style, block.align);
  const borderColor = block.backgroundColor || block.textColor || "#82378A";

  return {
    wrapper: {
      ...styles.wrapper,
    },
    button: {
      ...styles.button,
      ...(block.backgroundColor ? { background: block.backgroundColor } : {}),
      ...(block.textColor ? { color: block.textColor } : {}),
      borderRadius: Number(block.borderRadius || 12),
      borderColor,
      width: block.fullWidth ? "100%" : styles.button.width,
    },
  };
};

const getBlockContainerStyle = (highlighted) => ({
  position: "relative",
  borderRadius: 18,
  padding: highlighted ? 5 : 0,
  marginBottom: 6,
  border: highlighted ? "2px solid rgba(130,55,138,0.78)" : "2px solid transparent",
  background: highlighted ? "rgba(130,55,138,0.06)" : "transparent",
  boxShadow: highlighted ? "0 14px 30px rgba(130,55,138,0.16)" : "none",
  transition: "all 0.2s ease",
  scrollMarginTop: 110,
});

const SectionBlocksRenderer = ({ section, language, sectionsByCode, getUrlPath }) => {
  const { previewMode, previewHighlightedBlockId, placeholders: hotelPlaceholders } = useContext(HotelContext);
  const { data: checkInData } = useContext(CheckInContext);
  const placeholdersMerged = useMemo(
    () => mergeHotelAndCheckinPlaceholders(hotelPlaceholders, checkInData?.placeholders),
    [hotelPlaceholders, checkInData?.placeholders]
  );
  const containerRef = useRef(null);
  const lastAutoScrolledHighlightRef = useRef("");
  const { pathname } = useLocation();

  const blocks = useMemo(() => {
    return normalizeContentBlocks(section?.extras?.contentBlocks);
  }, [section?.extras?.contentBlocks]);

  useEffect(() => {
    if (!previewMode || !previewHighlightedBlockId || !containerRef.current) {
      return;
    }

    const currentSelectionKey = `${section?.code || "no-section"}:${previewHighlightedBlockId}`;
    if (lastAutoScrolledHighlightRef.current === currentSelectionKey) {
      return;
    }

    const highlightedNode = containerRef.current.querySelector(`[data-preview-block-id="${previewHighlightedBlockId}"]`);
    if (!highlightedNode) {
      return;
    }

    lastAutoScrolledHighlightRef.current = currentSelectionKey;

    const scrollableParent = getScrollableParent(highlightedNode);
    if (!scrollableParent) {
      return;
    }

    const parentRect = scrollableParent.getBoundingClientRect();
    const nodeRect = highlightedNode.getBoundingClientRect();
    const desiredOffset = Math.max((scrollableParent.clientHeight - nodeRect.height) / 2, 24);
    const nextTop = scrollableParent.scrollTop + (nodeRect.top - parentRect.top) - desiredOffset;

    scrollableParent.scrollTo({
      top: Math.max(nextTop, 0),
      behavior: "smooth",
    });
  }, [previewHighlightedBlockId, previewMode, section?.code]);

  if (!blocks.length) {
    return null;
  }

  return (
    <div ref={containerRef} style={{ marginTop: 20 }}>
      {blocks.map((block) => {
        if (!block?.id || !block?.type) {
          return null;
        }

        const highlighted = previewMode && previewHighlightedBlockId === block.id;
        let content = null;

        if (block.type === "heading") {
          const HeadingTag = block.level || "h2";
          const text = i18nValue(block, "text", language, placeholdersMerged);

          if (!text) {
            return null;
          }

          content = (
            <HeadingTag
              style={{
                textAlign: block.align || "left",
                marginBottom: getBlockSpacing(block.spacingBottom, 16),
                color: block.textColor || "#181C32",
              }}
            >
              {text}
            </HeadingTag>
          );
        }

        if (block.type === "text") {
          const html = i18nValue(block, "html", language, placeholdersMerged);
          if (!html) {
            return null;
          }

          content = (
            <div
              style={{
                textAlign: block.align || "left",
                marginBottom: getBlockSpacing(block.spacingBottom, 18),
                color: block.textColor || "#3F4254",
                lineHeight: 1.7,
                fontSize: getFontSizeValue(block.fontSize),
              }}
            >
              {processHtml(html)}
            </div>
          );
        }

        if (block.type === "image") {
          const image = getBlockImage(block.image);
          if (!image) {
            return null;
          }

          const caption = i18nValue(block, "caption", language, placeholdersMerged);
          const alt = i18nValue(block, "alt", language, placeholdersMerged) || caption || "";

          content = (
            <div style={{ marginBottom: getBlockSpacing(block.spacingBottom, 20) }}>
              <img
                src={image}
                alt={alt}
                style={{
                  width: "100%",
                  display: "block",
                  borderRadius: Number(block.borderRadius || 16),
                  objectFit: block.objectFit || "cover",
                  height: Number(block.imageHeight || 0) > 0 ? Number(block.imageHeight) : "auto",
                }}
              />
              {caption ? (
                <small style={{ display: "block", marginTop: 8, color: "#7E8299" }}>{caption}</small>
              ) : null}
            </div>
          );
        }

        if (block.type === "button") {
          const label = i18nValue(block, "label", language, placeholdersMerged);
          if (!label) {
            return null;
          }

          const styles = getCustomButtonStyles(block);
          const internalTarget =
            block.actionType === "section" && block.sectionCode && sectionsByCode?.[block.sectionCode]
              ? getUrlPath(block.sectionCode)
              : block.actionType === "food_menu" && block.foodMenuId
                ? getUrlPath(`menu/${block.foodMenuId}`)
                : "";

          if (internalTarget) {
            content = (
              <div style={styles.wrapper}>
                <NavLink to={internalTarget} state={{ from: pathname }} style={styles.button}>
                  {label}
                </NavLink>
              </div>
            );
          } else if (block.href) {
            content = (
              <div style={styles.wrapper}>
                <a
                  href={block.href}
                  target={block.newTab ? "_blank" : undefined}
                  rel={block.newTab ? "noreferrer" : undefined}
                  style={styles.button}
                >
                  {label}
                </a>
              </div>
            );
          } else {
            return null;
          }
        }

        if (block.type === "menu_commerce") {
          content = <MenuCommerceModule block={block} language={language} />;
        }

        if (block.type === "separator") {
          content = (
            <div
              style={{
                marginTop: Number(block.spacingTop || 0),
                marginBottom: Number(block.spacingBottom || 0),
                borderTop:
                  block.style === "dashed"
                    ? `${Number(block.thickness || 1)}px dashed ${block.color || "rgba(130,55,138,0.35)"}`
                    : block.style === "dotted"
                      ? `${Number(block.thickness || 1)}px dotted ${block.color || "rgba(130,55,138,0.35)"}`
                      : `${Number(block.thickness || 1)}px solid ${block.color || "rgba(130,55,138,0.18)"}`,
              }}
            />
          );
        }

        if (block.type === "spacer") {
          content = <div style={{ height: Number(block.height || 0) }} />;
        }

        if (block.type === "video") {
          const embedUrl = getVideoEmbedUrl(block.url);
          if (!embedUrl) {
            return null;
          }

          const caption = i18nValue(block, "caption", language, placeholdersMerged);

          content = (
            <div style={{ marginBottom: getBlockSpacing(block.spacingBottom, 22) }}>
              <div
                style={{
                  position: "relative",
                  paddingTop: "56.25%",
                  borderRadius: Number(block.borderRadius || 16),
                  overflow: "hidden",
                  background: "#111827",
                }}
              >
                <iframe
                  title={caption || embedUrl}
                  src={embedUrl}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              </div>
              {caption ? (
                <small style={{ display: "block", marginTop: 8, color: "#7E8299" }}>{caption}</small>
              ) : null}
            </div>
          );
        }

        if (block.type === "gallery") {
          const images = Array.isArray(block.images) ? block.images : [];
          if (!images.length) {
            return null;
          }

          content = (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(Math.max(Number(block.columns || 2), 1), 3)}, minmax(0, 1fr))`,
                gap: 10,
                marginBottom: getBlockSpacing(block.spacingBottom, 20),
              }}
            >
              {images.map((item, index) => {
                const image = getBlockImage(item?.image);
                if (!image) {
                  return null;
                }

                return (
                  <img
                    key={item?.id || `${image}-${index}`}
                    src={image}
                    alt=""
                    style={{
                      width: "100%",
                      height: Number(block.imageHeight || 140),
                      objectFit: "cover",
                      borderRadius: Number(block.borderRadius || 14),
                      display: "block",
                    }}
                  />
                );
              })}
            </div>
          );
        }

        if (block.type === "columns") {
          const leftTitle = i18nValue(block, "leftTitle", language, placeholdersMerged);
          const leftHtml = i18nValue(block, "leftHtml", language, placeholdersMerged);
          const rightTitle = i18nValue(block, "rightTitle", language, placeholdersMerged);
          const rightHtml = i18nValue(block, "rightHtml", language, placeholdersMerged);

          if (!leftTitle && !leftHtml && !rightTitle && !rightHtml) {
            return null;
          }

          content = (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: Number(block.gap || 16),
                marginBottom: getBlockSpacing(block.spacingBottom, 22),
              }}
            >
              <div>
                {leftTitle ? <h5 style={{ marginBottom: 10 }}>{leftTitle}</h5> : null}
                {leftHtml ? (
                  <div style={{ color: block.textColor || "#3F4254", lineHeight: 1.7 }}>{processHtml(leftHtml)}</div>
                ) : null}
              </div>
              <div>
                {rightTitle ? <h5 style={{ marginBottom: 10 }}>{rightTitle}</h5> : null}
                {rightHtml ? (
                  <div style={{ color: block.textColor || "#3F4254", lineHeight: 1.7 }}>{processHtml(rightHtml)}</div>
                ) : null}
              </div>
            </div>
          );
        }

        if (!content) {
          return null;
        }

        return (
          <div
            key={block.id}
            data-preview-block-id={block.id}
            style={getBlockContainerStyle(highlighted)}
          >
            {content}
          </div>
        );
      })}
    </div>
  );
};

export default SectionBlocksRenderer;
